import appConstants from '@/plugins/constants';
import { groupLocationsByAssignmentNumber } from './assignmentGrouping.js';
import {
  buildAccountLocationIndex,
  filterGlcodesForLocation,
} from './glLocationValidation.js';
import { buildLocationTypeById } from '@/modules/financialclose/utils/locationMasterType';
import { resolveEnforceThreshold } from '@/modules/financialclose/utils/enforceThresholdHelpers';
import { coerceEvidenceRequired } from '@/modules/financialclose/utils/constants';

const TASK_TYPES = appConstants.TASK_LIST_CONSTANTS.TASK_TYPES;

/**
 * API validates GL codes against Chart of Accounts by account number (`coa.glcode`),
 * not Mongo `_id`. Resolve selected values to glcode strings.
 */
export function normalizeTemplateGlcodes(glcodes = [], chartOfAccounts = []) {
  const byId = new Map();
  const byGlcode = new Map();

  (chartOfAccounts || []).forEach((coa) => {
    if (!coa) return;
    const gl = coa.glcode != null ? String(coa.glcode).trim() : '';
    if (coa._id != null) byId.set(String(coa._id), gl || String(coa._id));
    if (coa.id != null) byId.set(String(coa.id), gl || String(coa.id));
    if (gl) byGlcode.set(gl, gl);
  });

  const out = [];
  (glcodes || []).forEach((item) => {
    if (item == null || item === '') return;

    if (typeof item === 'object') {
      const fromObj =
        (item.glcode != null && String(item.glcode).trim()) ||
        byId.get(String(item._id || item.id || '')) ||
        '';
      if (fromObj) out.push(fromObj);
      return;
    }

    const key = String(item).trim();
    if (!key) return;
    if (byGlcode.has(key)) {
      out.push(key);
      return;
    }
    if (byId.has(key)) {
      out.push(byId.get(key));
      return;
    }
    out.push(key);
  });

  return [...new Set(out.filter(Boolean))];
}

/**
 * Expand UI assignment blocks into API location[] rows (one row per location id).
 * Assignment UI shows the union of GLs for the block. Each persisted entity
 * row receives only GLs mapped to that location. A consolidated location row
 * keeps every GL selected on the block.
 *
 * When selected GLs exist, entity locations that map to none of them are
 * omitted — not sent with `glcode: []`. The API rejects empty GL lists on
 * Account Reconciliation rows. Consolidated rows are kept with the full
 * selection. Preparer / approver / sign-off for an omitted site are not
 * persisted as their own row; they remain on sibling locations in the same
 * assignment block. When no GLs were selected, every location row is still
 * emitted with `glcode: []` (non-AR assignments).
 */
export function expandAssignmentsToLocationRows(
  assignments = [],
  chartOfAccounts = [],
  locations = [],
  taskType = ''
) {
  const isAr = taskType === TASK_TYPES.ACCOUNT_RECONCILIATION;
  const rows = [];
  const accountIndex = buildAccountLocationIndex(chartOfAccounts, locations);
  const locationTypeById = buildLocationTypeById(locations);

  (assignments || []).forEach((assignment) => {
    const parsed = Number(assignment?.assignment_number);
    const assignmentNumber = Number.isFinite(parsed) ? parsed : 1;
    const locationIds = assignment?.location_ids || [];
    const preparer = assignment?.preparer || [];
    const approver = assignment?.approver || [];
    const requiresSignOff = !!assignment?.requires_sign_off;
    const signOffApprovers = requiresSignOff
      ? [...(assignment?.sign_off_approvers || [])]
      : [];
    const enforceThreshold = isAr
      ? resolveEnforceThreshold(assignment, TASK_TYPES.ACCOUNT_RECONCILIATION)
      : false;
    const thresholdFields = { enforceThreshold };
    const autoApprove = !!assignment?.auto_approve;
    const glcodes = normalizeTemplateGlcodes(
      assignment?.glcodes || assignment?.glcode || [],
      chartOfAccounts
    );

    locationIds.forEach((locationId) => {
      const rowGlcodes = glcodes.length
        ? filterGlcodesForLocation({
            glcodes,
            locationId,
            locations,
            accountIndex,
            locationTypeById,
          })
        : [];

      const omitLocationWithoutGlcodes =
        glcodes.length > 0 && rowGlcodes.length === 0;
      if (omitLocationWithoutGlcodes) return;

      rows.push({
        location: locationId,
        location_id: locationId,
        preparer: [...preparer],
        approver: [...approver],
        requires_sign_off: requiresSignOff,
        sign_off_approvers: [...signOffApprovers],
        ...thresholdFields,
        auto_approve: autoApprove,
        assignment_number: assignmentNumber,
        glcode: [...rowGlcodes],
      });
    });
  });

  return rows;
}

/**
 * Build master checklist add/edit payload from form + assignment blocks.
 * GL codes live only on location rows — no root-level `glcode`.
 */
export function buildTemplatePayload(
  form = {},
  assignments = [],
  customFields = [],
  chartOfAccounts = [],
  locations = []
) {
  const location = expandAssignmentsToLocationRows(
    assignments,
    chartOfAccounts,
    locations,
    form.task_type
  );
  const isArTemplate = form.task_type === TASK_TYPES.ACCOUNT_RECONCILIATION;
  const cycleType = (form.cycle_type || form.cycleType || 'FBC').toUpperCase();

  const payload = {
    description: form.description || '',
    task_type: form.task_type,
    cycle_type: cycleType,
    category: form.category || '',
    subcategory: form.subcategory || '',
    tags: form.tags || form.frequency || [],
    duedays:
      form.duedays === '' || form.duedays == null
        ? 0
        : Number(form.duedays),
    priority: form.priority || '',
    no_of_evidence: form.noofevidence != null ? Number(form.noofevidence) : 0,
    evidence_required: coerceEvidenceRequired(form.evidence_required),
    assertions: form.assertion || form.assertions || [],
    purpose: form.purpose || '',
    instruction: form.instruction || '',
    location,
    enforceThreshold: isArTemplate ? resolveEnforceThreshold(form, form.task_type) : false,
  };

  if (form._id) payload._id = form._id;

  const customfields = [];
  (customFields || []).forEach((field) => {
    const key = field?.columnName;
    if (!key || field.isSystem) return;
    customfields.push({ [key]: form[key] != null ? form[key] : '' });
  });
  if (customfields.length) payload.customfields = customfields;

  return payload;
}

/**
 * Account numbers stored on the template (root `glcode` or assignment location rows).
 * Used when hydrating the Accounts tab so location-only GLs are not dropped.
 */
export function collectTemplateGlcodes(template = {}) {
  const fromRoot = Array.isArray(template.glcode)
    ? template.glcode
    : Array.isArray(template.selectgls)
      ? template.selectgls
      : [];
  const fromLocations = [];
  (Array.isArray(template.location) ? template.location : []).forEach((loc) => {
    const codes = loc?.glcode || loc?.glcodes || [];
    if (Array.isArray(codes)) fromLocations.push(...codes);
  });
  const seen = new Set();
  const out = [];
  [...fromRoot, ...fromLocations].forEach((raw) => {
    const value = raw != null ? String(raw).trim() : '';
    if (!value || seen.has(value)) return;
    seen.add(value);
    out.push(value);
  });
  return out;
}

/**
 * Hydrate assignment blocks from a template's location[] array.
 * Falls back to location_details + root preparer/approver from GET enrichment.
 */
export function assignmentsFromTemplate(template = {}) {
  const locationArr = Array.isArray(template.location) ? template.location : [];
  const taskType = template.task_type || template.taskType || '';
  const groups = groupLocationsByAssignmentNumber(locationArr, taskType);
  if (groups.length) return groups;

  const locationIds = (template.location_details || [])
    .map((l) => (l && (l._id || l.id) != null ? String(l._id || l.id) : ''))
    .filter(Boolean);
  const preparer = Array.isArray(template.preparer)
    ? template.preparer
        .map((u) => (typeof u === 'object' ? String(u._id || u.id || '') : String(u)))
        .filter(Boolean)
    : [];
  const approver = Array.isArray(template.approver)
    ? template.approver
        .map((u) => (typeof u === 'object' ? String(u._id || u.id || '') : String(u)))
        .filter(Boolean)
    : [];

  if (!locationIds.length && !preparer.length && !approver.length) return [];

  return [
    {
      assignment_number: 1,
      location_ids: [...new Set(locationIds)],
      preparer: [...new Set(preparer)],
      approver: [...new Set(approver)],
      enforceThreshold: resolveEnforceThreshold(template, taskType),
      auto_approve: false,
      glcodes: [],
    },
  ];
}

/**
 * Derive columnName from display name (Manage parity).
 */
export function columnNameFromDisplayName(displayName = '') {
  return displayName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');
}
